import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, ChevronDown, ChevronRight, Mail, MessageSquare, Phone, Plus, Sparkles, Star, Trash2, Users, Pencil, Check, Bell, Clock, Globe, ArrowRightLeft, MoreHorizontal, ExternalLink, History, Merge, SlidersHorizontal, Undo2 } from "lucide-react";
import { Link as RouterLink } from "react-router-dom";
import { humanizeLabel, type ContactDetail, type ContactFieldValue, type InteractionType, type Note, type Reminder, type TimelineEntry } from "@hub/shared";
import { Avatar, Button, Checkbox, EmptyState, IconButton, Input, Menu, Pill, Select, Spinner, formatDate, relativeTime, toast } from "@/ui";
import { useEngineMutation, useEngineQuery } from "@/engine/hooks";
import { getHost, hostMode } from "@/engine/transport";
import { ReachLink } from "./ReachLink";
import { ContactChip } from "@/features/people/ContactChip";
import { momentLink } from "./momentLinks";
import { NoteEditor } from "./NoteEditor";
import { ReminderEditor } from "./ReminderEditor";
import { describeDraft, draftFromState } from "./cadence";
import s from "../shared.module.css";

// Overview --------------------------------------------------------------------------------------------------------

export function Highlights({ detail }: { detail: ContactDetail }) {
  if (detail.highlights.length === 0) return null;
  return (
    <section className={s.section}>
      <div className="u-row" style={{ gap: 6, marginBottom: 6, fontWeight: 600 }}><Sparkles size={14} /> Highlights</div>
      <ul className={s.bullets}>
        {detail.highlights.map((h, i) => (
          <li key={i}>
            {h.text}
            {h.contactIds && h.contactIds.length > 0 && (
              <span className="u-row" style={{ display: "inline-flex", gap: 4, marginLeft: 6, verticalAlign: "middle" }}>
                {h.contactIds.slice(0, 4).map((id) => <ContactChip key={id} contactId={id} />)}
              </span>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

const SOURCE_NAMES: Record<string, string> = { apple_contacts: "Contacts", apple_calendar: "Calendar", apple_messages: "Messages", apple_calls: "Phone", google: "Google", microsoft: "Outlook", linkedin_csv: "LinkedIn", csv: "CSV", ics: "ICS", network: "Hub", manual: "You", apple_mail: "Mail", email: "Email" };

function Source({ source }: { source: string }) {
  const name = SOURCE_NAMES[source] ?? source.replace(/_/g, " ");
  return <span className={s.sourceTag} style={{ whiteSpace: "nowrap" }} title={`From ${source.replace(/_/g, " ")}`}>{name}</span>;
}

export function ProfileFields({ detail, compact = false }: { detail: ContactDetail; compact?: boolean }) {
  const { profile, lookups } = detail;
  // The side panel is too narrow for provenance badges; the person page shows them.
  const badge = (source: string) => (compact ? null : <Source source={source} />);
  const fieldValues = useEngineQuery("getContactFieldValues", { contactId: detail.contact.id });
  const sourceFor = (field: string, ordinal = 0) => fieldValues.data?.find((v) => v.fieldName === field && v.ordinal === ordinal)?.source;
  const emails = lookups.filter((l) => l.lookup.type === "email");
  const phones = lookups.filter((l) => l.lookup.type === "phone");
  const rows: { label: string; content: React.ReactNode }[] = [];
  if (emails.length) rows.push({ label: "Email", content: emails.map((l) => <span key={l.id} className="u-row" style={{ gap: 4 }}><ReachLink kind="email" value={l.lookup.value} />{humanizeLabel(l.label) && <span className="u-small u-muted">{humanizeLabel(l.label)}</span>}{l.isPrimary && emails.length > 1 && <Pill>primary</Pill>}{badge(l.source)}</span>) });
  if (phones.length) rows.push({ label: "Phone", content: phones.map((l) => <span key={l.id} className="u-row" style={{ gap: 4 }}><ReachLink kind="phone" value={l.lookup.value} />{humanizeLabel(l.label) && <span className="u-small u-muted">{humanizeLabel(l.label)}</span>}{badge(l.source)}</span>) });
  if (profile.workHistory.length) rows.push({ label: "Work", content: <div className="u-col" style={{ gap: 4 }}>{profile.workHistory.map((w, i) => <span key={i} className="u-row" style={{ gap: 6 }}><span>{[w.title, w.company].filter(Boolean).join(" at ")}</span><span className="u-small u-muted">{[w.startDate, w.isCurrent ? "now" : w.endDate].filter(Boolean).join(" to ")}</span>{sourceFor("work_history", i) && badge(sourceFor("work_history", i)!)}</span>)}</div> });
  if (profile.education.length) rows.push({ label: "Education", content: profile.education.map((e, i) => <span key={i}>{[e.school, e.degree, e.field].filter(Boolean).join(", ")}{e.endYear ? ` (${e.endYear})` : ""}</span>) });
  if (profile.addresses.length) rows.push({ label: "Location", content: profile.addresses.map((a, i) => <span key={i} className="u-row" style={{ gap: 4 }}>{[a.street, a.city, a.region, a.country].filter(Boolean).join(", ")}{sourceFor("address", i) && badge(sourceFor("address", i)!)}</span>) });
  if (profile.birthday) rows.push({ label: "Birthday", content: <span>{new Date(2000, profile.birthday.month - 1, profile.birthday.day).toLocaleDateString(undefined, { month: "long", day: "numeric" })}{profile.birthday.year ? `, ${profile.birthday.year}` : ""}</span> });
  if (profile.socialLinks.length) rows.push({ label: "Social", content: profile.socialLinks.map((l) => <a key={l.url} href={l.url} target="_blank" rel="noreferrer" className="u-row" style={{ gap: 4 }}>{l.platform}{l.handle ? ` @${l.handle}` : ""}<ExternalLink size={11} /></a>) });
  if (profile.websites.length) rows.push({ label: "Websites", content: profile.websites.map((w) => <a key={w} href={w} target="_blank" rel="noreferrer" className="u-row" style={{ gap: 4 }}><Globe size={12} />{w.replace(/^https?:\/\//, "")}</a>) });
  if (profile.interests.length) rows.push({ label: "Interests", content: profile.interests.map((i) => <Pill key={i}>{i}</Pill>) });
  if (profile.bio) rows.push({ label: "Bio", content: <span>{profile.bio}</span> });
  const conflicts = conflictingFields(fieldValues.data ?? []);
  if (rows.length === 0 && conflicts.length === 0) return <div className="u-muted u-small">No profile details yet. Edit the person to add some.</div>;
  return (
    <section className={s.section}>
      <dl className={s.kv} style={{ margin: 0 }}>
        {rows.map((r) => (
          <div key={r.label} style={{ display: "contents" }}>
            <dt style={{ textTransform: "capitalize" }}>{r.label}</dt>
            <dd>{r.content}</dd>
          </div>
        ))}
      </dl>
      {conflicts.length > 0 && <SourcePickers contactId={detail.contact.id} conflicts={conflicts} />}
    </section>
  );
}

export interface FieldConflict {
  fieldName: string;
  values: { source: string; value: string }[];
}

/** Fields that several sources filled in with different values (structured JSON values are summarised). */
export function conflictingFields(values: ContactFieldValue[]): FieldConflict[] {
  const byField = new Map<string, Map<string, string>>();
  for (const v of values) {
    if (v.deleted || v.ordinal !== 0) continue;
    const m = byField.get(v.fieldName) ?? new Map<string, string>();
    if (!m.has(v.source)) m.set(v.source, v.value);
    byField.set(v.fieldName, m);
  }
  const out: FieldConflict[] = [];
  for (const [fieldName, m] of byField) {
    // Only genuinely different, non-empty values are a disagreement, and a value typed by hand already settled it.
    if (m.get("manual")?.trim()) continue;
    const distinct = new Set(Array.from(m.values()).map((v) => v.trim()).filter(Boolean));
    if (distinct.size < 2) continue;
    out.push({ fieldName, values: Array.from(m, ([source, value]) => ({ source, value: summariseValue(value) })).filter((v) => v.value.trim()) });
  }
  return out.sort((a, b) => a.fieldName.localeCompare(b.fieldName));
}

function summariseValue(value: string): string {
  if (!value.startsWith("{")) return value;
  try {
    const o = JSON.parse(value) as Record<string, unknown>;
    return Object.values(o).filter((x) => typeof x === "string" && x).join(", ");
  } catch {
    return value;
  }
}

function SourcePickers({ contactId, conflicts }: { contactId: string; conflicts: FieldConflict[] }) {
  const [chosen, setChosen] = useState<Record<string, string>>({});
  const setOverride = useEngineMutation("setProfileOverride", { invalidate: ["getContact", "getContactProfile", "getContactFieldValues", "queryContacts", "getContactRows"] });
  const clearOverride = useEngineMutation("clearProfileOverride", { invalidate: ["getContact", "getContactProfile", "getContactFieldValues", "queryContacts", "getContactRows"] });
  return (
    <div style={{ marginTop: 10, padding: 8, border: "1px solid var(--border)", borderRadius: 8, background: "var(--bg-sunken)" }} data-testid="source-pickers">
      <div className="u-row u-small" style={{ gap: 6, fontWeight: 600, marginBottom: 4 }}><SlidersHorizontal size={12} /> Several sources disagree</div>
      <div className="u-small u-muted" style={{ marginBottom: 6 }}>Pick which source Hub should show for each field, or leave it automatic (the freshest source wins).</div>
      {conflicts.map((c) => (
        <div key={c.fieldName} className="u-row" style={{ gap: 8, padding: "4px 0", flexWrap: "wrap" }}>
          <span style={{ textTransform: "capitalize", minWidth: 90 }} className="u-small">{c.fieldName.replace(/_/g, " ")}</span>
          <span className="u-grow u-small u-muted u-truncate" title={c.values.map((v) => `${v.source}: ${v.value}`).join("\n")}>{c.values.map((v) => `${v.source.replace(/_/g, " ")}: ${v.value}`).join(" / ")}</span>
          <Select
            aria-label={`Source for ${c.fieldName}`}
            value={chosen[c.fieldName] ?? ""}
            style={{ width: 150, height: 26 }}
            onChange={(e) => {
              const source = e.target.value;
              setChosen((m) => ({ ...m, [c.fieldName]: source }));
              if (source) setOverride.mutate({ contactId, fieldName: c.fieldName, source }, { onSuccess: () => toast(`Showing ${source.replace(/_/g, " ")} for ${c.fieldName.replace(/_/g, " ")}`) });
              else clearOverride.mutate({ contactId, fieldName: c.fieldName }, { onSuccess: () => toast("Back to automatic") });
            }}
          >
            <option value="">Automatic</option>
            {c.values.map((v) => <option key={v.source} value={v.source}>{v.source.replace(/_/g, " ")}</option>)}
          </Select>
        </div>
      ))}
    </div>
  );
}

/** Custom fields defined in Settings, with this person's values from the profile. */
export function CustomFieldsBlock({ detail }: { detail: ContactDetail }) {
  const defs = useEngineQuery("getCustomFieldDefinitions", undefined);
  const fields = detail.profile.fields;
  const defined = defs.data ?? [];
  const extraKeys = Object.keys(fields).filter((k) => !defined.some((d) => d.fieldName === k));
  if (defined.length === 0 && extraKeys.length === 0) return null;
  const render = (v: string | string[] | undefined, type?: string) => {
    if (v === undefined || v === "" || (Array.isArray(v) && v.length === 0)) return <span className="u-muted u-small">Not set</span>;
    if (type === "checkbox") return <span>{v === "true" || v === "1" ? "Yes" : "No"}</span>;
    if (type === "url" && typeof v === "string") return <a href={v} target="_blank" rel="noreferrer" className="u-row" style={{ gap: 4 }}>{v.replace(/^https?:\/\//, "")}<ExternalLink size={11} /></a>;
    if (type === "date" && typeof v === "string") return <span>{formatDate(v)}</span>;
    return Array.isArray(v) ? <span className="u-row" style={{ gap: 4, flexWrap: "wrap" }}>{v.map((x) => <Pill key={x}>{x}</Pill>)}</span> : <span>{v}</span>;
  };
  return (
    <section className={s.section} data-testid="custom-fields">
      <div className="u-row" style={{ justifyContent: "space-between", marginBottom: 6 }}>
        <span style={{ fontWeight: 600 }}>Custom</span>
        <RouterLink to="/settings/fields" className="u-small">Manage fields</RouterLink>
      </div>
      <dl className={s.kv} style={{ margin: 0 }}>
        {defined.map((d) => (
          <div key={d.fieldName} style={{ display: "contents" }}>
            <dt>{d.displayName}</dt>
            <dd>{render(fields[d.fieldName], d.fieldType)}</dd>
          </div>
        ))}
        {extraKeys.map((k) => (
          <div key={k} style={{ display: "contents" }}>
            <dt style={{ textTransform: "capitalize" }}>{k.replace(/_/g, " ")}</dt>
            <dd>{render(fields[k])}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

/** Collapsed by default: every recorded profile change (field, before, after, source, when). */
export function ProfileHistory({ contactId }: { contactId: string }) {
  const [open, setOpen] = useState(false);
  const log = useEngineQuery("getContactProfileChangelog", { contactId }, { enabled: open });
  return (
    <section className={s.section} data-testid="profile-history">
      <button type="button" onClick={() => setOpen((o) => !o)} className="u-row" style={{ gap: 6, border: "none", background: "transparent", padding: 0, cursor: "pointer", color: "inherit", fontWeight: 600, fontFamily: "inherit", fontSize: "inherit" }} aria-expanded={open}>
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <History size={14} /> History
      </button>
      {open && (
        <div style={{ marginTop: 8 }}>
          {log.isLoading && <Spinner />}
          {log.isSuccess && log.data.length === 0 && <div className="u-small u-muted">No profile changes recorded yet. Changes from imports and enrichment land here.</div>}
          {(log.data ?? []).map((e, i) => (
            <div key={i} className="u-row" style={{ gap: 8, padding: "6px 0", borderBottom: "1px solid var(--border)", alignItems: "flex-start", flexWrap: "wrap" }}>
              <span style={{ minWidth: 90, textTransform: "capitalize" }} className="u-small">{e.fieldName.replace(/_/g, " ")}</span>
              <span className="u-grow u-small" style={{ minWidth: 0 }}>
                {e.oldValue ? <span className="u-muted" style={{ textDecoration: "line-through", marginRight: 6 }}>{summariseValue(e.oldValue)}</span> : null}
                <span>{summariseValue(e.newValue)}</span>
              </span>
              <Source source={e.source} />
              <span className="u-small u-muted" title={e.changedAt}>{relativeTime(e.changedAt)}</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export function RelatedPeople({ detail }: { detail: ContactDetail }) {
  const [adding, setAdding] = useState(false);
  const [q, setQ] = useState("");
  const [label, setLabel] = useState("");
  const labels = useEngineQuery("getRelatedContactLabels", undefined);
  const search = useEngineQuery("queryContacts", { query: q, limit: 6 }, { enabled: adding && q.trim().length > 0 });
  const add = useEngineMutation("addRelatedContact", { invalidate: ["getContact"] });
  const remove = useEngineMutation("removeRelatedContact", { invalidate: ["getContact"] });
  const setLabelM = useEngineMutation("setRelatedContactLabel", { invalidate: ["getContact"] });
  const removeLabel = useEngineMutation("removeRelatedContactLabel", { invalidate: ["getContact"] });
  return (
    <section className={s.section}>
      <div className="u-row" style={{ justifyContent: "space-between", marginBottom: 6 }}>
        <span className="u-row" style={{ gap: 6, fontWeight: 600 }}><Users size={14} /> Related people</span>
        <Button size="sm" variant="ghost" icon={<Plus size={13} />} onClick={() => setAdding((a) => !a)}>Add</Button>
      </div>
      {adding && (
        <div className="u-col" style={{ gap: 6, marginBottom: 8, padding: 8, border: "1px solid var(--border)", borderRadius: 8 }}>
          <div className="u-row">
            <Input autoFocus placeholder="Search a person" value={q} onChange={(e) => setQ(e.target.value)} />
            <Select value={label} onChange={(e) => setLabel(e.target.value)} style={{ width: 150 }} aria-label="Relationship label">
              <option value="">No label</option>
              {(labels.data ?? []).map((l) => <option key={l} value={l}>{l}</option>)}
            </Select>
          </div>
          {(search.data?.items ?? []).filter((r) => r.contactId !== detail.contact.id).map((r) => (
            <button key={r.contactId} type="button" className={s.chip} data-size="md" style={{ justifyContent: "flex-start" }} onClick={() => add.mutate({ contactId: detail.contact.id, relatedContactId: r.contactId, label: label || null }, { onSuccess: () => { setAdding(false); setQ(""); } })}>
              <Avatar name={r.displayName} src={r.avatarUrl} size={22} seed={r.contactId} /> {r.displayName} <span className="u-muted u-small">{r.headline}</span>
            </button>
          ))}
        </div>
      )}
      {detail.related.length === 0 && !adding && <div className="u-muted u-small">Link family, colleagues or whoever introduced you.</div>}
      <div className="u-col" style={{ gap: 6 }}>
        {detail.related.map((r) => (
          <div key={r.relatedContactId} className="u-row">
            <ContactChip contactId={r.relatedContactId} size="md" />
            {r.labels.map((l) => (
              <Pill key={l} tone="accent">
                {l}
                <button type="button" aria-label={`Remove label ${l}`} onClick={() => removeLabel.mutate({ contactId: detail.contact.id, relatedContactId: r.relatedContactId, label: l })} style={{ border: "none", background: "transparent", cursor: "pointer", color: "inherit", padding: 0, display: "inline-flex" }}>x</button>
              </Pill>
            ))}
            <Menu
              align="right"
              trigger={<IconButton label="Related person options"><MoreHorizontal size={14} /></IconButton>}
              items={[
                ...(labels.data ?? []).filter((l) => !r.labels.includes(l)).map((l) => ({ id: l, label: `Label: ${l}`, onSelect: () => setLabelM.mutate({ contactId: detail.contact.id, relatedContactId: r.relatedContactId, label: l }) })),
                { id: "d", label: "", divider: true },
                { id: "rm", label: "Remove relation", danger: true, onSelect: () => remove.mutate({ contactId: detail.contact.id, relatedContactId: r.relatedContactId }) },
              ]}
            />
          </div>
        ))}
      </div>
    </section>
  );
}

export function GroupsSection({ detail, onAdd }: { detail: ContactDetail; onAdd: () => void }) {
  const remove = useEngineMutation("removeContactsFromGroup", { invalidate: ["getContact", "listGroups", "getContactsInGroup", "queryContacts"] });
  return (
    <section className={s.section}>
      <div className="u-row" style={{ justifyContent: "space-between", marginBottom: 6 }}>
        <span style={{ fontWeight: 600 }}>Groups</span>
        <Button size="sm" variant="ghost" icon={<Plus size={13} />} onClick={onAdd}>Add (l)</Button>
      </div>
      <div className="u-row" style={{ gap: 6, flexWrap: "wrap" }}>
        {detail.groups.length === 0 && <span className="u-muted u-small">Not in any group yet.</span>}
        {detail.groups.map((g) => (
          <Link key={g.id} to={`/group/${g.id}`} className={s.chip} data-size="md" style={{ textDecoration: "none" }}>
            <span style={{ width: 12, height: 12, borderRadius: 3, background: g.color ?? "var(--text-tertiary)", marginLeft: 4 }} />
            {g.name}
            {g.kind === "smart" ? <span className="u-small u-muted">smart</span> : (
              <button type="button" aria-label={`Remove from ${g.name}`} onClick={(e) => { e.preventDefault(); remove.mutate({ groupId: g.id, contactIds: [detail.contact.id] }); }} style={{ border: "none", background: "transparent", cursor: "pointer", color: "var(--text-tertiary)", padding: 0, display: "inline-flex" }}>x</button>
            )}
          </Link>
        ))}
      </div>
    </section>
  );
}

export function CadenceSummary({ detail, onEdit }: { detail: ContactDetail; onEdit: () => void }) {
  const resolved = useEngineQuery("getResolvedReconnectCadence", { contactId: detail.contact.id });
  return (
    <section className={s.section}>
      <div className="u-row" style={{ justifyContent: "space-between" }}>
        <span className="u-row" style={{ gap: 6, fontWeight: 600 }}><Clock size={14} /> Reconnect</span>
        <Button size="sm" variant="ghost" icon={<Pencil size={13} />} onClick={onEdit}>Edit (h)</Button>
      </div>
      <div className="u-small u-muted" style={{ marginTop: 4 }}>
        {describeDraft(draftFromState(detail.cadence))}
        {resolved.data?.nextDue && resolved.data.state !== "disabled" ? ` Next suggestion around ${formatDate(resolved.data.nextDue)}.` : ""}
      </div>
    </section>
  );
}

// Timeline --------------------------------------------------------------------------------------------------------

const TYPE_LABEL: Record<InteractionType, string> = { email: "Emails", event: "Meetings", message_day: "Messages", call: "Calls", hub_message: "Hub messages", manual: "Logged" };

function iconFor(type: InteractionType) {
  switch (type) {
    case "email": return <Mail size={13} />;
    case "event": return <Calendar size={13} />;
    case "message_day": return <MessageSquare size={13} />;
    case "call": return <Phone size={13} />;
    default: return <Star size={13} />;
  }
}

/** Opens the original message or event: through the desktop shell when present, otherwise a normal external link. */
function MomentOpenLink({ href, label }: { href: string; label: string }) {
  const host = getHost();
  if (hostMode() === "desktop" && host?.shell?.openExternal) {
    return (
      <button type="button" className="u-row" style={{ gap: 3, border: "none", background: "transparent", padding: 0, cursor: "pointer", color: "var(--accent)", font: "inherit" }} onClick={() => host.shell!.openExternal(href).catch(() => toast("Could not open that", "danger"))} title={href} data-testid="moment-open">
        {label} <ExternalLink size={11} />
      </button>
    );
  }
  return (
    <a href={href} target="_blank" rel="noreferrer" className="u-row" style={{ gap: 3 }} title={href} data-testid="moment-open">
      {label} <ExternalLink size={11} />
    </a>
  );
}

export function TimelineEntryView({ entry }: { entry: TimelineEntry }) {
  const i = entry.interaction;
  const who = entry.participants.map((p) => p.name).filter(Boolean).slice(0, 3).join(", ");
  let title = "";
  let body: string | null = null;
  if (entry.email) { title = entry.email.subject ?? "Email"; body = `${entry.email.isFromMe ? "You wrote" : "They wrote"}${entry.email.snippet ? `: ${entry.email.snippet}` : ""}`; }
  else if (entry.event) { title = entry.event.title ?? "Meeting"; body = `${new Date(entry.event.startDatetime).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}${entry.event.endDatetime ? ` to ${new Date(entry.event.endDatetime).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}` : ""}${entry.event.participantCount ? `, ${entry.event.participantCount} people` : ""}`; }
  else if (entry.messageDay) { title = `${entry.messageDay.messageCount} messages on ${entry.messageDay.platform === "imessage" ? "iMessage" : entry.messageDay.platform}`; body = `${entry.messageDay.messagesFromMe} from you, ${entry.messageDay.messagesToMe} to you. Message content is never stored.`; }
  else if (entry.call) { title = `${entry.call.outgoing ? "Outgoing" : "Incoming"} ${entry.call.mediaType} call`; body = entry.call.endDatetime ? `${Math.round((new Date(entry.call.endDatetime).getTime() - new Date(entry.call.startDatetime).getTime()) / 60_000)} minutes` : null; }
  else title = entry.note?.plainText ?? "Interaction";
  const link = momentLink(entry);
  return (
    <div className={s.timelineItem}>
      <span className={s.timelineIcon}>{iconFor(i.type)}</span>
      <div className="u-grow" style={{ minWidth: 0 }}>
        <div className="u-row" style={{ justifyContent: "space-between" }}>
          <span style={{ fontWeight: 500 }} className="u-truncate">{title}</span>
          <span className="u-row u-small u-muted" style={{ flex: "none", gap: 8 }}>
            {link && <MomentOpenLink href={link.href} label={link.label} />}
            <span title={i.date}>{formatDate(i.date, { month: "short", day: "numeric", year: new Date(i.date).getFullYear() !== new Date().getFullYear() ? "numeric" : undefined })}</span>
          </span>
        </div>
        {body && <div className="u-small u-muted u-truncate">{body}</div>}
        {who && entry.participants.length > 1 && <div className="u-small u-muted">With {who}</div>}
      </div>
    </div>
  );
}

export function TimelineSection({ contactId }: { contactId: string }) {
  const [types, setTypes] = useState<InteractionType[]>([]);
  const [limit, setLimit] = useState(30);
  const [aheadLimit, setAheadLimit] = useState(5);
  // Fixed for the life of the page so the queries keep one key; what is ahead sits under its own heading, next first.
  const [now] = useState(() => new Date().toISOString());
  const filters = useEngineQuery("getContactTimelineFilters", { contactId });
  const typeFilter = types.length ? types : undefined;
  const ahead = useEngineQuery("getContactTimeline", { contactId, filter: { types: typeFilter, from: now }, order: "oldest", limit: aheadLimit, offset: 0 }, { placeholderData: (p) => p });
  const timeline = useEngineQuery("getContactTimeline", { contactId, filter: { types: typeFilter, to: now }, limit, offset: 0 }, { placeholderData: (p) => p });
  const toggle = (t: InteractionType) => setTypes((ts) => (ts.includes(t) ? ts.filter((x) => x !== t) : [...ts, t]));
  return (
    <section className={s.section}>
      <div className="u-row" style={{ gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
        {(filters.data?.types ?? []).map((t) => (
          <button key={t} type="button" className={s.hint} style={{ fontFamily: "var(--font)", background: types.includes(t) ? "var(--accent-soft)" : undefined, color: types.includes(t) ? "var(--accent)" : undefined }} onClick={() => toggle(t)} aria-pressed={types.includes(t)}>
            {iconFor(t)} {TYPE_LABEL[t]}
          </button>
        ))}
      </div>
      {timeline.isLoading && <Spinner />}
      {!!ahead.data?.items.length && (
        <div style={{ marginBottom: 12 }} data-testid="timeline-ahead">
          <div className="u-small u-muted" style={{ fontWeight: 600, marginBottom: 4 }}>Ahead</div>
          {ahead.data.items.map((e) => <TimelineEntryView key={e.interaction.id} entry={e} />)}
          {ahead.data.nextOffset !== null && (
            <Button variant="ghost" size="sm" icon={<ChevronDown size={14} />} onClick={() => setAheadLimit((l) => l + 10)} loading={ahead.isFetching}>
              Show more ({ahead.data.total - ahead.data.items.length} left)
            </Button>
          )}
        </div>
      )}
      {timeline.data?.items.length === 0 && !ahead.data?.items.length && <EmptyState title="No interactions yet">Emails, meetings, messages and calls with this person appear here once a source is connected.</EmptyState>}
      {timeline.data?.items.map((e) => <TimelineEntryView key={e.interaction.id} entry={e} />)}
      {timeline.data && timeline.data.nextOffset !== null && (
        <Button variant="ghost" full style={{ marginTop: 8 }} icon={<ChevronDown size={14} />} onClick={() => setLimit((l) => l + 30)} loading={timeline.isFetching}>
          Show more ({timeline.data.total - timeline.data.items.length} left)
        </Button>
      )}
    </section>
  );
}

// Notes -----------------------------------------------------------------------------------------------------------

export function NoteCard({ note, contactName, onEdit, onDelete, showContact }: { note: Note; contactName?: string | null; onEdit?: () => void; onDelete?: () => void; showContact?: boolean }) {
  return (
    <article className={s.noteCard}>
      <div className={s.noteText}>{note.plainText}</div>
      <div className={s.noteMeta}>
        {showContact && note.contactId && <ContactChip contactId={note.contactId} />}
        {showContact && contactName && !note.contactId && <span>{contactName}</span>}
        <span title={note.created}>{relativeTime(note.created)}</span>
        {note.label && <Pill tone="accent">{note.label}</Pill>}
        <span className="u-grow" />
        {onEdit && <IconButton label="Edit note" onClick={onEdit}><Pencil size={13} /></IconButton>}
        {onDelete && <IconButton label="Delete note" onClick={onDelete}><Trash2 size={13} /></IconButton>}
      </div>
    </article>
  );
}

export function NotesSection({ contactId, notes }: { contactId: string; notes: Note[] }) {
  const [editing, setEditing] = useState<Note | null | "new">(null);
  const del = useEngineMutation("deleteNote", { invalidate: ["getContact", "getAllNotes", "getContactNotes", "getNoteLabels"] });
  return (
    <section className={s.section}>
      <div className="u-row" style={{ justifyContent: "space-between", marginBottom: 8 }}>
        <span style={{ fontWeight: 600 }}>Notes</span>
        <Button size="sm" icon={<Plus size={13} />} onClick={() => setEditing("new")}>New note (n)</Button>
      </div>
      {notes.length === 0 && <div className="u-muted u-small">Nothing written down yet. Notes are searchable and dates in them can become reminders.</div>}
      {notes.map((n) => (
        <NoteCard key={n.id} note={n} onEdit={() => setEditing(n)} onDelete={() => { if (window.confirm("Delete this note?")) del.mutate({ noteId: n.id }); }} />
      ))}
      <NoteEditor open={editing !== null} onClose={() => setEditing(null)} contactId={contactId} note={editing === "new" ? null : editing} />
    </section>
  );
}

// Reminders -------------------------------------------------------------------------------------------------------

export function ReminderRow({ reminder, onEdit, showContact }: { reminder: Reminder & { contact?: { contactId: string; displayName: string } | null }; onEdit?: () => void; showContact?: boolean }) {
  const mark = useEngineMutation("markReminderProcessed", { invalidate: ["getReminders", "getContact", "getPendingReminders", "getTimeline"] });
  const del = useEngineMutation("deleteReminder", { invalidate: ["getReminders", "getContact", "getPendingReminders", "getTimeline"] });
  const overdue = !reminder.isProcessed && new Date(reminder.fireAt).getTime() < Date.now();
  return (
    <div className="u-row" style={{ padding: "8px 0", borderBottom: "1px solid var(--border)", gap: 10 }}>
      <Checkbox checked={reminder.isProcessed} onChange={(e) => mark.mutate({ reminderId: reminder.id, processed: e.target.checked })} aria-label="Done" />
      <div className="u-grow" style={{ minWidth: 0, textDecoration: reminder.isProcessed ? "line-through" : undefined, color: reminder.isProcessed ? "var(--text-tertiary)" : undefined }}>
        <div className="u-truncate">{reminder.title ?? "Reminder"}</div>
        <div className="u-small u-row" style={{ gap: 8, color: overdue ? "var(--danger)" : "var(--text-tertiary)" }}>
          <Bell size={11} /> {formatDate(reminder.fireAt, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
          {overdue && <span>overdue</span>}
          {showContact && reminder.contactId && <ContactChip contactId={reminder.contactId} />}
        </div>
      </div>
      {onEdit && <IconButton label="Edit reminder" onClick={onEdit}><Pencil size={13} /></IconButton>}
      <IconButton label="Delete reminder" onClick={() => del.mutate({ reminderId: reminder.id })}><Trash2 size={13} /></IconButton>
    </div>
  );
}

export function RemindersSection({ contactId, reminders }: { contactId: string; reminders: Reminder[] }) {
  const [editing, setEditing] = useState<Reminder | null | "new">(null);
  const [showDone, setShowDone] = useState(false);
  const shown = useMemo(() => reminders.filter((r) => showDone || !r.isProcessed), [reminders, showDone]);
  return (
    <section className={s.section}>
      <div className="u-row" style={{ justifyContent: "space-between", marginBottom: 8 }}>
        <span style={{ fontWeight: 600 }}>Reminders</span>
        <span className="u-row">
          <Button size="sm" variant="ghost" onClick={() => setShowDone((v) => !v)}>{showDone ? "Hide done" : "Show done"}</Button>
          <Button size="sm" icon={<Plus size={13} />} onClick={() => setEditing("new")}>New reminder (r)</Button>
        </span>
      </div>
      {shown.length === 0 && <div className="u-muted u-small">No reminders. Hub notifies you here and on your desktop; it never messages the person.</div>}
      {shown.map((r) => <ReminderRow key={r.id} reminder={r} onEdit={() => setEditing(r)} />)}
      <ReminderEditor open={editing !== null} onClose={() => setEditing(null)} contactId={contactId} reminder={editing === "new" ? null : editing} />
    </section>
  );
}

// Sources ---------------------------------------------------------------------------------------------------------

/** Records merged into this person, each with an Undo that restores the archived copy. */
export function MergedRecords({ contactId }: { contactId: string }) {
  const merges = useEngineQuery("listMerges", { contactId });
  const unmerge = useEngineMutation("unmergeContact", { invalidate: ["getContact", "listMerges", "queryContacts", "searchArchivedContacts", "getContactRows", "listPotentialDuplicates"] });
  const active = (merges.data ?? []).filter((m) => !m.undone && m.targetContactId === contactId);
  if (active.length === 0) return null;
  return (
    <section className={s.section} data-testid="merged-records">
      <div className="u-row" style={{ gap: 6, fontWeight: 600, marginBottom: 6 }}><Merge size={14} /> Merged records</div>
      <div className="u-small u-muted" style={{ marginBottom: 6 }}>Other records folded into this person. Undo restores the record with everything that came from it.</div>
      {active.map((m) => (
        <div key={m.id} className="u-row" style={{ padding: "6px 0", borderBottom: "1px solid var(--border)" }}>
          <span className="u-grow u-small">Merged a record {relativeTime(m.created)}: {m.mergedLookupIds.length} source{m.mergedLookupIds.length === 1 ? "" : "s"}, {m.mergedNoteIds.length} note{m.mergedNoteIds.length === 1 ? "" : "s"}</span>
          <Button size="sm" variant="outline" icon={<Undo2 size={13} />} loading={unmerge.isPending} onClick={() => unmerge.mutate({ mergeId: m.id }, { onSuccess: () => toast("Merge undone", "success") })}>Undo</Button>
        </div>
      ))}
    </section>
  );
}

export function SourcesSection({ detail }: { detail: ContactDetail }) {
  const [adding, setAdding] = useState(false);
  const [type, setType] = useState<"email" | "phone" | "linkedin" | "twitter" | "instagram" | "website">("email");
  const [value, setValue] = useState("");
  const [moveId, setMoveId] = useState<string | null>(null);
  const [moveQuery, setMoveQuery] = useState("");
  const add = useEngineMutation("addContactLookup", { invalidate: ["getContact", "getContactLookups", "queryContacts"] });
  const remove = useEngineMutation("removeContactLookup", { invalidate: ["getContact", "queryContacts"] });
  const setLabel = useEngineMutation("setContactLookupLabel", { invalidate: ["getContact"] });
  const setPrimary = useEngineMutation("setContactLookupPrimary", { invalidate: ["getContact", "queryContacts"] });
  const move = useEngineMutation("moveContactLookup", { invalidate: ["getContact", "queryContacts"] });
  const moveNew = useEngineMutation("moveContactLookupToNewContact", { invalidate: ["getContact", "queryContacts"] });
  const search = useEngineQuery("queryContacts", { query: moveQuery, limit: 6 }, { enabled: !!moveId && moveQuery.trim().length > 0 });
  const [conflict, setConflict] = useState<string[] | null>(null);

  return (
    <section className={s.section}>
      <div className="u-row" style={{ justifyContent: "space-between", marginBottom: 8 }}>
        <span style={{ fontWeight: 600 }}>Sources</span>
        <Button size="sm" variant="ghost" icon={<Plus size={13} />} onClick={() => setAdding((a) => !a)}>Add</Button>
      </div>
      <div className="u-small u-muted" style={{ marginBottom: 8 }}>Identifiers Hub uses to recognise this person in emails, calendars and imports.</div>
      {adding && (
        <div className="u-row" style={{ marginBottom: 8 }}>
          <Select value={type} onChange={(e) => setType(e.target.value as typeof type)} style={{ width: 120 }} aria-label="Type">
            {["email", "phone", "linkedin", "twitter", "instagram", "website"].map((t) => <option key={t} value={t}>{t}</option>)}
          </Select>
          <Input autoFocus value={value} onChange={(e) => setValue(e.target.value)} placeholder={type === "email" ? "name@company.com" : type === "phone" ? "+1 415 555 0100" : "handle or URL"} onKeyDown={(e) => e.key === "Enter" && value.trim() && add.mutate({ contactId: detail.contact.id, type, value: value.trim() }, { onSuccess: (r) => { setValue(""); setAdding(false); if (r.conflicts.length) setConflict(r.conflicts); else toast("Source added", "success"); } })} />
          <Button icon={<Check size={14} />} disabled={!value.trim()} onClick={() => add.mutate({ contactId: detail.contact.id, type, value: value.trim() }, { onSuccess: (r) => { setValue(""); setAdding(false); if (r.conflicts.length) setConflict(r.conflicts); else toast("Source added", "success"); } })}>Add</Button>
        </div>
      )}
      {conflict && (
        <div style={{ padding: 10, border: "1px solid var(--warning)", borderRadius: 8, marginBottom: 8, background: "var(--warning-soft)" }}>
          <div style={{ fontWeight: 600 }}>This identifier also belongs to</div>
          <div className="u-row" style={{ gap: 6, marginTop: 4, flexWrap: "wrap" }}>{conflict.map((id) => <ContactChip key={id} contactId={id} />)}</div>
          <div className="u-small" style={{ marginTop: 6 }}>They may be the same person. You can merge them from the menu.</div>
          <Button size="sm" variant="ghost" onClick={() => setConflict(null)} style={{ marginTop: 6 }}>Dismiss</Button>
        </div>
      )}
      {detail.lookups.map((l) => (
        <div key={l.id} className="u-row" style={{ padding: "6px 0", borderBottom: "1px solid var(--border)" }}>
          <Pill>{l.lookup.type.replace(/_/g, " ")}</Pill>
          <span className="u-grow u-truncate" title={l.lookup.value}>{l.lookup.value}</span>
          {l.label && <span className="u-small u-muted">{l.label}</span>}
          {l.isPrimary && <Pill tone="accent">primary</Pill>}
          <Source source={l.source} />
          <Menu
            align="right"
            trigger={<IconButton label="Source options"><MoreHorizontal size={14} /></IconButton>}
            items={[
              { id: "primary", label: "Make primary", disabled: l.isPrimary, onSelect: () => setPrimary.mutate({ contactLookupId: l.id }) },
              { id: "label", label: "Set label", onSelect: () => { const v = window.prompt("Label for this source (work, personal, ...)", l.label ?? ""); if (v !== null) setLabel.mutate({ contactLookupId: l.id, label: v.trim() || null }); } },
              { id: "move", label: "Move to another person", icon: <ArrowRightLeft size={13} />, onSelect: () => setMoveId(l.id) },
              { id: "split", label: "Move to a new person", onSelect: () => moveNew.mutate({ contactLookupId: l.id }, { onSuccess: () => toast("Moved to a new person") }) },
              { id: "d", label: "", divider: true },
              { id: "remove", label: "Remove source", danger: true, onSelect: () => { if (window.confirm("Remove this identifier from the person?")) remove.mutate({ contactLookupId: l.id }); } },
            ]}
          />
        </div>
      ))}
      {moveId && (
        <div style={{ marginTop: 8, padding: 8, border: "1px solid var(--border)", borderRadius: 8 }} className="u-col">
          <div className="u-row" style={{ justifyContent: "space-between" }}><strong className="u-small">Move source to</strong><Button size="sm" variant="ghost" onClick={() => setMoveId(null)}>Cancel</Button></div>
          <Input autoFocus placeholder="Search a person" value={moveQuery} onChange={(e) => setMoveQuery(e.target.value)} />
          {(search.data?.items ?? []).filter((r) => r.contactId !== detail.contact.id).map((r) => (
            <button key={r.contactId} type="button" className={s.chip} data-size="md" style={{ justifyContent: "flex-start" }} onClick={() => move.mutate({ contactLookupId: moveId, toContactId: r.contactId }, { onSuccess: () => { setMoveId(null); setMoveQuery(""); toast(`Moved to ${r.displayName}`); } })}>
              <Avatar name={r.displayName} src={r.avatarUrl} size={22} seed={r.contactId} /> {r.displayName}
            </button>
          ))}
        </div>
      )}
    </section>
  );
}
